import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { TrendingUp, TrendingDown, DollarSign, BarChart3, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import api from '@/lib/api';

export default function FinancialAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canonical, setCanonical] = useState(null); // V5.2 · fonte canônica

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      // V5.2 · shadow mode canônico (?engine=legacy desativa)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const year = new Date().getFullYear();
      const [response, canonicalRes] = await Promise.all([
        api.get('/financial/analytics'),
        useLegacy
          ? Promise.resolve({ data: null })
          : api.get('/finance/executive', { params: { year } }).catch(() => ({ data: null })),
      ]);
      setAnalytics(response.data);
      if (canonicalRes.data) {
        setCanonical(canonicalRes.data);
        console.info('[analytics] motor canônico', canonicalRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar análises financeiras');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!analytics || analytics.total_months === 0) {
    if (canonical) {
      // V5.2 · Fallback canônico: renderiza cards mínimos usando db.titles
      return (
        <div className="p-6 space-y-6" data-testid="financial-analytics-canonical">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Análises Financeiras</h1>
            <p className="text-muted-foreground mt-1">
              Fonte canônica · db.titles (sem histórico mensal legacy ainda)
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Recebido</CardTitle></CardHeader>
              <CardContent><div className="text-3xl font-bold text-green-700">{formatCurrency(canonical.recebido)}</div></CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2"><CardTitle className="text-sm">A Receber</CardTitle></CardHeader>
              <CardContent><div className="text-3xl font-bold">{formatCurrency(canonical.a_receber)}</div></CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Vencido</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{formatCurrency(canonical.vencido)}</div>
                <p className="text-xs text-muted-foreground mt-1">{canonical.inadimplencia_pct}% inadimplência</p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Nenhum dado financeiro disponível</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const chartData = analytics.data.map(d => ({
    month: d.month,
    receita: d.gross_revenue,
    ebitda: d.ebitda,
    margem: d.margin_percentage,
    valuation: d.enterprise_value / 1000000, // Em milhões
  }));

  const isCritical = analytics.latest_month && analytics.latest_month.ebitda < 0;

  return (
    <div className="p-6 space-y-6" data-testid="financial-analytics-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Análises Financeiras</h1>
        <p className="text-muted-foreground mt-1">Visão temporal de Receita, EBITDA e Valuation</p>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className={`border-border/60 ${isCritical ? 'border-destructive/30 bg-destructive/5' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">EBITDA Médio</CardTitle>
            {analytics.trend === 'positive' ? (
              <TrendingUp className="h-5 w-5 text-secondary" />
            ) : (
              <TrendingDown className="h-5 w-5 text-destructive" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${analytics.avg_ebitda < 0 ? 'text-destructive' : 'text-secondary'}`}>
              {formatCurrency(analytics.avg_ebitda)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.total_months} meses analisados
            </p>
            {isCritical && (
              <Badge variant="destructive" className="mt-2">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Crítico
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Receita Total</CardTitle>
            <DollarSign className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="analytics-receita">
              {formatCurrency(canonical?.recebido ?? analytics.total_revenue)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {canonical ? <span className="text-purple-600">·canônico · db.titles</span> : `Média: ${formatCurrency(analytics.avg_revenue)}/mês`}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valuation (EV)</CardTitle>
            <BarChart3 className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${analytics.latest_valuation < 0 ? 'text-destructive' : ''}`}>
              {formatCurrency(analytics.latest_valuation)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Enterprise Value atual
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Receita e EBITDA */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Receita e EBITDA Mensal</CardTitle>
          <CardDescription>Evolução ao longo do tempo</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Area type="monotone" dataKey="receita" stroke="hsl(265, 89%, 60%)" fill="hsl(265, 89%, 60%)" fillOpacity={0.3} name="Receita Bruta" />
              <Area type="monotone" dataKey="ebitda" stroke="hsl(150, 80%, 45%)" fill="hsl(150, 80%, 45%)" fillOpacity={0.3} name="EBITDA" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gráfico de Margem */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Margem EBITDA (%)</CardTitle>
          <CardDescription>Eficiência operacional por mês</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
              <Bar dataKey="margem" fill="hsl(265, 89%, 60%)" name="Margem %" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gráfico de Valuation */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Enterprise Value (R$ Milhões)</CardTitle>
          <CardDescription>Valuation da empresa ao longo do tempo</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => `R$ ${value.toFixed(2)}M`} />
              <Line type="monotone" dataKey="valuation" stroke="hsl(150, 80%, 45%)" strokeWidth={2} name="EV (Milhões)" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detalhes do Último Mês */}
      {analytics.latest_month && (
        <Card className={`border-border/60 ${isCritical ? 'border-destructive/30 bg-destructive/5' : ''}`}>
          <CardHeader>
            <CardTitle>Detalhes do Último Mês ({analytics.latest_month.month})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Receita Bruta</p>
                <p className="text-2xl font-bold">{formatCurrency(analytics.latest_month.gross_revenue)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">EBITDA</p>
                <p className={`text-2xl font-bold ${analytics.latest_month.ebitda < 0 ? 'text-destructive' : 'text-secondary'}`}>
                  {formatCurrency(analytics.latest_month.ebitda)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Margem</p>
                <p className={`text-2xl font-bold ${analytics.latest_month.margin_percentage < 0 ? 'text-destructive' : ''}`}>
                  {analytics.latest_month.margin_percentage.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inadimplência</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {analytics.latest_month.default_percentage.toFixed(1)}%
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-6 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Custos Fixos</p>
                <p className="text-lg font-medium">{formatCurrency(analytics.latest_month.fixed_costs)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Custos Diretos</p>
                <p className="text-lg font-medium">{formatCurrency(analytics.latest_month.direct_costs)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Dívida</p>
                <p className="text-lg font-medium">{formatCurrency(analytics.latest_month.debt)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Caixa</p>
                <p className="text-lg font-medium text-secondary">{formatCurrency(analytics.latest_month.cash)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
